import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Video, ShieldAlert, EyeOff, ClipboardList, Award, Users, Save } from "lucide-react";
import { toast } from "sonner";

const Section = ({ icon: Icon, title, desc, children, testId }) => (
  <Card className="border-0 shadow-sm" data-testid={testId}>
    <CardContent className="p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg grid place-items-center bg-navy/5 text-navy shrink-0" style={{ color: "#00214D" }}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <h3 className="font-semibold text-base">{title}</h3>
          {desc && <p className="text-xs text-neutral-500 mt-0.5">{desc}</p>}
        </div>
      </div>
      <div className="pl-12 space-y-4">{children}</div>
    </CardContent>
  </Card>
);

const Toggle = ({ checked, onChange, label, desc, testId, badge }) => (
  <div className="flex items-start justify-between gap-4">
    <div>
      <div className="flex items-center gap-2 text-sm font-medium">
        {label}
        {badge && <Badge className="text-[9px] bg-pink text-white border-0">{badge}</Badge>}
      </div>
      {desc && <p className="text-xs text-neutral-500 mt-0.5 max-w-xl">{desc}</p>}
    </div>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

export default function JobSettingsPage() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { api.get("/recruit/job-settings").then(r => setS(r.data)).catch(() => {}); }, []);

  const upd = (path, value) => {
    setS(prev => {
      const next = { ...prev, [path[0]]: { ...(prev[path[0]] || {}), [path[1]]: value } };
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/recruit/job-settings", {
        ai_assistant: s.ai_assistant,
        one_way_ai: s.one_way_ai,
        proctoring: s.proctoring,
        candidate_videos: s.candidate_videos,
        defaults: s.defaults,
      });
      setS(data);
      setDirty(false);
      toast.success("Job settings saved");
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="text-neutral-500 py-16 text-center">Loading settings…</div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="job-settings-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Recruit</div>
          <h1 className="text-3xl font-bold font-display mt-1">Job settings</h1>
          <p className="text-neutral-600 mt-1 text-sm">Defaults applied to all new jobs. Override per-job as needed.</p>
        </div>
        <Button className="btn-primary rounded-full" onClick={save} disabled={saving || !dirty} data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </Button>
      </div>

      <Section icon={Sparkles} title="AI Assistant" desc="AI Assistant helps your team get started faster with assessment setup — applies recruiting best practices so your team can evaluate skills and create a great candidate experience." testId="section-ai-assistant">
        <Toggle
          checked={s.ai_assistant?.enable_suggestions}
          onChange={(v) => upd(["ai_assistant", "enable_suggestions"], v)}
          label="Enable suggestions based on company information"
          desc="Suggestions rely exclusively on company and job data, such as the job description."
          testId="toggle-ai-suggestions"
        />
        <div className="text-xs text-neutral-600 space-y-1 pl-1">
          <div className="flex items-center gap-2">✓ Question set for One-Way Video Interview</div>
          <div className="flex items-center gap-2 text-neutral-400">✓ Behavioral profile for Predictive Behavioral Assessment <Badge variant="outline" className="text-[9px]">Coming soon</Badge></div>
        </div>
      </Section>

      <Section icon={Video} title="One-Way Video Interview" desc="Alfred AI for one-way video: transcribe, summarize, and rank candidate responses." testId="section-one-way-ai">
        <Toggle
          checked={s.one_way_ai?.transcriptions}
          onChange={(v) => upd(["one_way_ai", "transcriptions"], v)}
          label="Transcriptions"
          desc="Jump to keywords in candidate responses and watch video playback with synchronized text."
          testId="toggle-transcriptions"
        />
        <Toggle
          checked={s.one_way_ai?.ai_summary}
          onChange={(v) => upd(["one_way_ai", "ai_summary"], v)}
          label="Candidate Summary"
          desc="Short, clear summaries let you review key insights and decide which interviews to watch."
          testId="toggle-ai-summary"
        />
        <Toggle
          checked={s.one_way_ai?.ai_video_review}
          onChange={(v) => upd(["one_way_ai", "ai_video_review"], v)}
          label="AI Video Review"
          desc="Interview responses are ranked by key competencies and traits so you watch the strongest first."
          testId="toggle-ai-video-review"
        />
      </Section>

      <Section icon={ShieldAlert} title="Proctoring" desc="Proctoring is designed to help your team quickly spot signals of possible misconduct during candidate interviews." testId="section-proctoring">
        <Toggle
          checked={s.proctoring?.flag_ai_generated}
          onChange={(v) => upd(["proctoring", "flag_ai_generated"], v)}
          label="Flag AI-generated content"
          desc="Identify candidate submissions that may require closer review due to possible AI use."
          testId="toggle-proctoring"
          badge="Beta"
        />
      </Section>

      <Section icon={EyeOff} title="Candidate videos" desc="Hide candidate videos from reviewers during evaluation. When enabled, reviewers will only hear candidate responses without seeing the video." testId="section-candidate-videos">
        <Toggle
          checked={s.candidate_videos?.hide_from_reviewers}
          onChange={(v) => upd(["candidate_videos", "hide_from_reviewers"], v)}
          label="Hide candidate videos from reviewers during the evaluation process"
          desc="When enabled, reviewers will only hear candidate responses without seeing the video, both in the app and when using share links."
          testId="toggle-hide-videos"
        />
      </Section>

      <div className="pt-2">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3">Job defaults</div>
        <p className="text-neutral-600 mb-4 text-sm">Set defaults for all jobs. Override them on individual jobs as needed.</p>
      </div>

      <Section icon={ClipboardList} title="Questionnaire" desc="Prompt candidates to provide or update necessary details for their evaluation." testId="section-questionnaire">
        <Toggle
          checked={s.defaults?.questionnaire_enabled}
          onChange={(v) => upd(["defaults", "questionnaire_enabled"], v)}
          label="Set a default questionnaire"
          desc="Prompt candidates to provide or update necessary details for their evaluation."
          testId="toggle-questionnaire"
        />
        {s.defaults?.questionnaire_enabled && (
          <div className="text-sm text-neutral-700 border rounded-lg px-3 py-2 bg-white flex items-center justify-between">
            <span><span className="font-medium">Company default</span> <span className="text-neutral-500">(currently: {s.defaults?.questionnaire_name || "Company default"})</span></span>
          </div>
        )}
      </Section>

      <Section icon={Award} title="Scorecard" desc="Rate candidates systematically against the job's requirements." testId="section-scorecard">
        <Toggle
          checked={s.defaults?.scorecard_enabled}
          onChange={(v) => upd(["defaults", "scorecard_enabled"], v)}
          label="Enable default scorecard on new jobs"
          desc="New jobs will start with Alfred's default scorecard traits (Communication, Technical, Culture, Leadership)."
          testId="toggle-scorecard"
        />
      </Section>

      <Section icon={Users} title="Permissions" desc="Determine who can access assessments and information related to a job. Changes here do not affect existing jobs." testId="section-permissions">
        <div>
          <div className="text-sm font-medium mb-1.5">Job visibility</div>
          <Select value={s.defaults?.job_visibility || "Shared"} onValueChange={(v) => upd(["defaults", "job_visibility"], v)}>
            <SelectTrigger className="w-full" data-testid="select-visibility"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Shared">Shared — everyone on the team can see this job</SelectItem>
              <SelectItem value="Private">Private — only invited members</SelectItem>
              <SelectItem value="Restricted">Restricted — recruiters + hiring managers only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Section>
    </div>
  );
}
